import { useMemo, useState } from 'react'
import { type FieldErrors, type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { optionalPhone } from '@/lib/phone'
import {
	Alert, AlertDescription,
	Button,
	ConfirmDialog,
	Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
	Input,
	RadioGroup,
	Sheet, SheetContent,
	Textarea,
} from '@rentflowdev/ui'
import { Step2Details } from '@/components/add-property-dialog/step-2-details'
import {
	applyFieldResets,
	TYPE_SPECIFIC_RESETS,
} from '@/components/add-property-dialog/type-change-resets'
import { optionalNumber } from '@/components/add-property-dialog/schema'
import {
	ADDITIONAL_CATEGORY_VALUES,
	COWORKING_EXTRA_VALUES,
	COWORKING_SERVICE_VALUES,
	EQUIPMENT_VALUES,
	FOOD_AND_DRINK_VALUES,
	KEY_AMENITY_VALUES,
	LAND_CATEGORY_VALUES,
	RESIDENTIAL_PARKING_VALUES,
	ROOM_ADJACENCY_VALUES,
	WALLS_TYPE_VALUES,
} from '@/components/add-property-dialog/enum-options'
import { RadioOption } from '@/components/add-property-dialog/form-options'
import { AddressSuggest } from '@/components/address-suggest'
import { MapPicker } from '@/components/map-picker'
import type { ResolvedAddress } from '@/lib/geo-address'
import { IS_MAPS_CONFIGURED } from '@/lib/maps-config'
import { useUpdatePropertyMutation } from '@/api'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { useCloseGuard } from '@/lib/use-close-guard'
import { extractErrorMessage } from '@/lib/error-message'
import type { Property } from '@/types'
import { CREATE_PROPERTY_TYPES } from '@/types'

type ElevatorCount = 'NONE' | '1' | '2' | '3' | '4'

/**
 * Число лифтов — выбор из пяти вариантов, а колонка в базе целая и без потолка.
 *
 * Всё, что больше четырёх, раньше возвращалось как «4», и башня с шестью
 * лифтами теряла два при первом же сохранении по любому поводу. Такое значение
 * список выразить не может, поэтому оно остаётся невыбранным — а `undefined` в
 * запросе означает «не менять», и хранимое число уцелеет.
 */
const elevatorToEnum = (n?: number): ElevatorCount | undefined => {
	if (n === undefined || n === null) return undefined
	if (n === 0) return 'NONE'
	if (n >= 1 && n <= 4) return String(n) as ElevatorCount
	return undefined
}

const elevatorFromEnum = (v: ElevatorCount | undefined): number | undefined => {
	if (v === undefined) return undefined
	return v === 'NONE' ? 0 : Number(v)
}

const editSchema = z.object({
	type:               z.enum(['APARTMENT', 'ROOM', 'HOUSE', 'OFFICE', 'LAND', 'RETAIL', 'WAREHOUSE', 'GARAGE', 'COWORKING']).optional(),
	// step-2 fields — names match wizard's AddPropertyFormValues so Step2Details renders them
	housingType:        z.enum(['APARTMENT', 'APARTMENTS']).optional(),
	roomLocation:       z.enum(['APARTMENT', 'HOSTEL', 'HOTEL']).optional(),
	roomsInApartment:   optionalNumber(z.coerce.number().int()),
	roomHousingType:    z.enum(['ROOM', 'BED']).optional(),
	bedCount:           optionalNumber(z.coerce.number().int()),
	sleepingPlaces:     optionalNumber(z.coerce.number().int()),
	eventsAllowed:      z.boolean().optional(),
	fireplace:          z.boolean().optional(),
	houseKind:          z.enum(['HOUSE', 'DACHA', 'COTTAGE', 'TOWNHOUSE']).optional(),
	wallsType:          z.enum(WALLS_TYPE_VALUES).optional(),
	sauna:              z.boolean().optional(),
	pool:               z.boolean().optional(),
	terrace:            z.boolean().optional(),
	plotArea:           optionalNumber(),
	landCategory:       z.enum(LAND_CATEGORY_VALUES).optional(),
	bathroomLocations:  z.array(z.enum(['INSIDE', 'OUTSIDE'])).default([]),
	communications:     z.array(z.string()).optional(),
	transportAccess:    z.array(z.string()).optional(),
	nearbyInfrastructure: z.array(z.string()).optional(),
	floor:              optionalNumber(z.coerce.number().int()),
	rooms:              optionalNumber(z.coerce.number().int().min(0)),
	roomType:           z.enum(ROOM_ADJACENCY_VALUES).optional(),
	balcony:            z.boolean().optional(),
	loggia:             z.boolean().optional(),
	wardrobe:           z.boolean().optional(),
	totalArea:          optionalNumber(z.coerce.number().min(0)),
	livingArea:         optionalNumber(z.coerce.number().min(0)),
	kitchenSpace:       optionalNumber(z.coerce.number().min(0)),
	bathroomType:       z.enum(['COMBINED', 'SEPARATE', 'SEVERAL']).optional(),
	bathroomCount:      optionalNumber(z.coerce.number().int().min(1)),
	renovation:         z.enum(['NONE', 'COSMETIC', 'EURO', 'DESIGNER']).optional(),
	furniture:          z.array(z.string()).optional(),
	appliances:         z.array(z.string()).optional(),
	internet:           z.array(z.string()).optional(),
	buildingType:       z.enum(['BRICK', 'PANEL', 'BLOCK', 'MONOLITH', 'MONOLITH_BRICK', 'WOOD', 'OTHER']).optional(),
	concierge:          z.boolean().optional(),
	chute:              z.boolean().optional(),
	gas:                z.boolean().optional(),
	yearBuilt:          optionalNumber(z.coerce.number().int().min(1800).max(2100)),
	totalFloors:        optionalNumber(z.coerce.number().int().min(0)),
	passengerElevators: z.enum(['NONE', '1', '2', '3', '4']).optional(),
	freightElevators:   z.enum(['NONE', '1', '2', '3', '4']).optional(),
	yard:               z.array(z.string()).optional(),
	parking:            z.array(z.string()).optional(),
	parkingType:        z.enum(RESIDENTIAL_PARKING_VALUES).optional(),
	maxTenants:         optionalNumber(z.coerce.number().int().min(0)),
	childrenAllowed:    z.boolean().optional(),
	petsAllowed:        z.boolean().optional(),
	smokingAllowed:     z.boolean().optional(),

	// location + description — sheet-specific
	city:        z.string().trim().min(1, 'Укажите город'),
	address:     z.string().trim().min(1, 'Укажите адрес'),
	latitude:    optionalNumber(),
	longitude:   optionalNumber(),
	description: z.string().optional(),

	// listing contact + apartment number — no dedicated UI in the sheet yet,
	// round-tripped so the update does not drop them
	apartmentNumber: z.string().trim().optional(),
	ownerType:       z.enum(['OWNER', 'AGENT']).optional(),
	phone:           optionalPhone('contacts.editDialog.validation.phoneInvalid'),
	// ── Коммерческие поля и гараж ────────────────────────────────────────
	// Step2Details рисует их по типу объекта; без объявления здесь zod вырезал
	// бы значения при сохранении — поля были бы видны и не сохранялись.
	onlineViewing:          z.boolean().optional(),
	multipleFloors:         z.boolean().optional(),
	officeLayouts:          z.array(z.enum(['CABINET', 'OPEN'])).default([]),
	partialRent:            z.boolean().optional(),
	ceilingHeight:          optionalNumber(z.coerce.number().positive()),
	finishing:              z.enum(['NONE', 'CLEAN', 'OFFICE']).optional(),
	powerCapacity:          optionalNumber(z.coerce.number().positive()),
	powerCapacityExpandable: z.boolean().optional(),
	commercialBuildingType: z.enum(['BUSINESS_CENTER', 'SHOPPING_CENTER', 'ADMINISTRATIVE', 'RESIDENTIAL_BUILDING', 'OTHER']).optional(),
	buildingClass:          z.enum(['A', 'B', 'C']).optional(),
	commercialParking:      z.enum(['NONE', 'STREET', 'IN_BUILDING']).optional(),
	parkingFree:            z.boolean().optional(),
	parkingForTrucks:       z.boolean().optional(),
	parkingSpaces:          optionalNumber(z.coerce.number().int().min(0)),
	entrance:               z.enum(['STREET', 'YARD']).optional(),
	separateEntrance:       z.boolean().optional(),
	commercialHeating:      z.enum(['NONE', 'CENTRAL', 'AUTONOMOUS']).optional(),
	retailPlacement:        z.enum(['STREET_RETAIL', 'IN_SHOPPING_CENTER']).optional(),
	additionalCategories:   z.array(z.enum(ADDITIONAL_CATEGORY_VALUES)).default([]),
	garageKind:             z.enum(['GARAGE', 'PARKING_SPACE']).optional(),
	garageType:             z.enum(['REINFORCED_CONCRETE', 'BRICK', 'METAL']).optional(),
	parkingSpaceType:       z.enum(['MULTILEVEL', 'UNDERGROUND', 'COVERED', 'OPEN']).optional(),
	security:               z.boolean().optional(),
	seatsCount:             optionalNumber(z.coerce.number().int().min(0)),
	weekendsOpen:           z.boolean().optional(),
	aroundTheClock:         z.boolean().optional(),
	workplaceStatus:        z.enum(['FIXED', 'FLOATING', 'PRIVATE_OFFICE']).optional(),
	privateOfficeArea:      optionalNumber(z.coerce.number().positive()),
	privateOfficeSeats:     optionalNumber(z.coerce.number().int().min(0)),
	amenitiesInclusion:     z.enum(['INCLUDED', 'NOT_INCLUDED', 'PARTIALLY_INCLUDED']).optional(),
	keyAmenities:           z.array(z.enum(KEY_AMENITY_VALUES)).default([]),
	equipment:              z.array(z.enum(EQUIPMENT_VALUES)).default([]),
	foodAndDrinks:          z.array(z.enum(FOOD_AND_DRINK_VALUES)).default([]),
	coworkingServices:      z.array(z.enum(COWORKING_SERVICE_VALUES)).default([]),
	coworkingExtras:        z.array(z.enum(COWORKING_EXTRA_VALUES)).default([]),
})

type EditPropertyFormValues = z.infer<typeof editSchema>

interface EditPropertySheetProps {
	property:     Property
	open:         boolean
	onOpenChange: (open: boolean) => void
}

export function EditPropertySheet({ property, open, onOpenChange }: EditPropertySheetProps) {
	const { t } = useTranslation()
	const [updateProperty, { isLoading }] = useUpdatePropertyMutation()
	const [submitError, setSubmitError]   = useState<string | null>(null)
	const [mapOpen, setMapOpen]           = useState(false)

	// Properties saved before address validation existed have no coordinates, and
	// editing an unrelated field must not force the user to re-pick the address.
	// So coordinates are demanded only when the address actually differs
	// from what the server returned. Compared against the server strings rather
	// than RHF's dirty state: the draft store hydrates from localStorage in
	// preference to server values, which makes `isDirty` unreliable here.
	const validationSchema = useMemo(
		() =>
			editSchema.superRefine((values, ctx) => {
				// Address only: `city` is an output of the geocoder, not an
				// independently resolvable input, and the sheet renders it as a
				// plain field with no suggester — demanding coordinates for a city
				// correction rejects the save with the error pinned to the
				// untouched address field, on every property predating geocoding.
				// Skipped entirely when no maps key is configured, since nothing
				// in that build can produce coordinates.
				const changed =
					values.address.trim() !== (property.address ?? '').trim()
				if (IS_MAPS_CONFIGURED && changed && (values.latitude == null || values.longitude == null)) {
					ctx.addIssue({
						code:    z.ZodIssueCode.custom,
						path:    ['address'],
						message: 'Выберите адрес из подсказок или укажите точку на карте',
					})
				}
			}),
		[property.address],
	)

	const { form, resetDraft } = usePersistedForm<EditPropertyFormValues>({
		key: `property:edit-sheet:${property.id}`,
		recordVersion: property.updatedAt,
		defaultValues: {
			// Через схему, а не через переписанный от руки союз: тот отстал на
			// пяти типах и заявлял, что торгового помещения здесь не бывает.
			type:               property.type as EditPropertyFormValues['type'],
			housingType:        property.housingType,
			roomLocation:       property.roomLocation,
			roomsInApartment:   property.roomsInApartment,
			roomHousingType:    property.roomHousingType,
			bedCount:           property.bedCount,
			sleepingPlaces:     property.sleepingPlaces,
			eventsAllowed:      property.eventsAllowed ?? false,
			fireplace:          property.fireplace     ?? false,
			houseKind:          property.houseKind,
			wallsType:          property.wallsType,
			sauna:              property.sauna   ?? false,
			pool:               property.pool    ?? false,
			terrace:            property.terrace ?? false,
			plotArea:           property.plotArea != null ? Number(property.plotArea) : undefined,
			landCategory:       property.landCategory,
			bathroomLocations:  property.bathroomLocations ?? [],

			// Коммерческие: правка начинается с того, что сохранено, а не с
			// пустой формы — иначе первое же сохранение стёрло бы их.
			officeLayouts: property.officeLayouts ?? [],
			additionalCategories: property.additionalCategories ?? [],
			keyAmenities: property.keyAmenities ?? [],
			equipment: property.equipment ?? [],
			foodAndDrinks: property.foodAndDrinks ?? [],
			coworkingServices: property.coworkingServices ?? [],
			coworkingExtras: property.coworkingExtras ?? [],
			ceilingHeight: property.ceilingHeight != null ? Number(property.ceilingHeight) : undefined,
			powerCapacity: property.powerCapacity != null ? Number(property.powerCapacity) : undefined,
			parkingSpaces: property.parkingSpaces != null ? Number(property.parkingSpaces) : undefined,
			seatsCount: property.seatsCount != null ? Number(property.seatsCount) : undefined,
			privateOfficeArea: property.privateOfficeArea != null ? Number(property.privateOfficeArea) : undefined,
			privateOfficeSeats: property.privateOfficeSeats != null ? Number(property.privateOfficeSeats) : undefined,
			onlineViewing: property.onlineViewing,
			multipleFloors: property.multipleFloors,
			partialRent: property.partialRent,
			finishing: property.finishing,
			powerCapacityExpandable: property.powerCapacityExpandable,
			commercialBuildingType: property.commercialBuildingType,
			buildingClass: property.buildingClass,
			commercialParking: property.commercialParking,
			parkingFree: property.parkingFree,
			parkingForTrucks: property.parkingForTrucks,
			entrance: property.entrance,
			separateEntrance: property.separateEntrance,
			commercialHeating: property.commercialHeating,
			retailPlacement: property.retailPlacement,
			garageKind: property.garageKind,
			garageType: property.garageType,
			parkingSpaceType: property.parkingSpaceType,
			security: property.security,
			weekendsOpen: property.weekendsOpen,
			aroundTheClock: property.aroundTheClock,
			workplaceStatus: property.workplaceStatus,
			amenitiesInclusion: property.amenitiesInclusion,
			communications:     property.communications     ?? [],
			transportAccess:    property.transportAccess    ?? [],
			nearbyInfrastructure: property.nearbyInfrastructure ?? [],
			apartmentNumber:    property.apartmentNumber,
			ownerType:          property.ownerType,
			phone:              property.phone,
			floor:              property.floor,
			rooms:              property.rooms,
			roomType:           property.roomType,
			balcony:            property.balcony  ?? false,
			loggia:             property.loggia   ?? false,
			wardrobe:           property.wardrobe ?? false,
			totalArea:          property.area         != null ? Number(property.area)         : undefined,
			livingArea:         property.livingArea   != null ? Number(property.livingArea)   : undefined,
			kitchenSpace:       property.kitchenSpace != null ? Number(property.kitchenSpace) : undefined,
			bathroomType:       property.bathroom,
			bathroomCount:      property.bathroomCount,
			renovation:         property.renovation,
			furniture:          property.furniture  ?? [],
			appliances:         property.appliances ?? [],
			internet:           property.internet   ?? [],
			buildingType:       property.buildingType,
			concierge:          property.concierge ?? false,
			chute:              property.chute     ?? false,
			gas:                property.gas       ?? false,
			yearBuilt:          property.yearBuilt,
			totalFloors:        property.totalFloors,
			passengerElevators: elevatorToEnum(property.passengerElevator),
			freightElevators:   elevatorToEnum(property.freightElevator),
			yard:               property.yard    ?? [],
			parking:            property.parking ?? [],
			parkingType:        property.parkingType,
			maxTenants:         property.maxTenants,
			childrenAllowed:    property.childrenAllowed ?? false,
			petsAllowed:        property.petsAllowed     ?? false,
			smokingAllowed:     property.smokingAllowed  ?? false,
			city:               property.city,
			address:            property.address,
			latitude:           property.latitude  != null ? Number(property.latitude)  : undefined,
			longitude:          property.longitude != null ? Number(property.longitude) : undefined,
			description:        property.description ?? '',
		},
		// Cast needed: schema preprocesses numeric fields, so zod's Input != Output.
		resolver: zodResolverI18n(validationSchema) as Resolver<EditPropertyFormValues>,
		mode:     'onBlur',
		active:   open,
	})

	// Same invariant as the wizard: only the geocoder writes coordinates, and
	// typing by hand drops them, so a hand-edited address cannot keep the pin
	// of the address it replaced.
	const handleAddressSelect = (r: ResolvedAddress) => {
		form.setValue('address',   r.address,   { shouldDirty: true, shouldValidate: true })
		form.setValue('latitude',  r.latitude,  { shouldDirty: true, shouldValidate: true })
		form.setValue('longitude', r.longitude, { shouldDirty: true, shouldValidate: true })
		if (r.city) form.setValue('city', r.city, { shouldDirty: true, shouldValidate: true })
	}

	const handleAddressTyped = (value: string) => {
		form.setValue('address', value, { shouldDirty: true })
		form.setValue('latitude',  undefined, { shouldDirty: true })
		form.setValue('longitude', undefined, { shouldDirty: true })
	}

	const { handleOpenChange, confirmOpen, handleConfirmDiscard, handleCancelDiscard } = useCloseGuard(
		form, onOpenChange, { isLoading, onReset: resetDraft },
	)

	async function onSubmit(values: EditPropertyFormValues) {
		setSubmitError(null)
		try {
			await updateProperty({
				id: property.id,
				body: {
					type:              values.type,
					housingType:       values.housingType,
					roomLocation:      values.roomLocation,
					roomsInApartment:  values.roomsInApartment,
					roomHousingType:   values.roomHousingType,
					bedCount:          values.bedCount,
					sleepingPlaces:    values.sleepingPlaces,
					eventsAllowed:     values.eventsAllowed,
					fireplace:         values.fireplace,
					houseKind:         values.houseKind,
					wallsType:         values.wallsType,
					sauna:             values.sauna,
					pool:              values.pool,
					terrace:           values.terrace,
					plotArea:          values.plotArea != null ? String(values.plotArea) : undefined,
					landCategory:      values.landCategory,
					bathroomLocations: values.bathroomLocations,

					// Коммерческие: сервер сам обнуляет их, когда объект
					// перестаёт быть коммерческим.
					onlineViewing: values.onlineViewing,
					multipleFloors: values.multipleFloors,
					officeLayouts: values.officeLayouts,
					partialRent: values.partialRent,
					ceilingHeight: values.ceilingHeight != null ? String(values.ceilingHeight) : undefined,
					finishing: values.finishing,
					powerCapacity: values.powerCapacity != null ? String(values.powerCapacity) : undefined,
					powerCapacityExpandable: values.powerCapacityExpandable,
					commercialBuildingType: values.commercialBuildingType,
					buildingClass: values.buildingClass,
					commercialParking: values.commercialParking,
					parkingFree: values.parkingFree,
					parkingForTrucks: values.parkingForTrucks,
					parkingSpaces: values.parkingSpaces,
					entrance: values.entrance,
					separateEntrance: values.separateEntrance,
					commercialHeating: values.commercialHeating,
					retailPlacement: values.retailPlacement,
					additionalCategories: values.additionalCategories,
					garageKind: values.garageKind,
					garageType: values.garageType,
					parkingSpaceType: values.parkingSpaceType,
					security: values.security,
					seatsCount: values.seatsCount,
					weekendsOpen: values.weekendsOpen,
					aroundTheClock: values.aroundTheClock,
					workplaceStatus: values.workplaceStatus,
					privateOfficeArea: values.privateOfficeArea != null ? String(values.privateOfficeArea) : undefined,
					privateOfficeSeats: values.privateOfficeSeats,
					amenitiesInclusion: values.amenitiesInclusion,
					keyAmenities: values.keyAmenities,
					equipment: values.equipment,
					foodAndDrinks: values.foodAndDrinks,
					coworkingServices: values.coworkingServices,
					coworkingExtras: values.coworkingExtras,
					communications:    values.communications,
					transportAccess:   values.transportAccess,
					nearbyInfrastructure: values.nearbyInfrastructure,
					apartmentNumber:   values.apartmentNumber,
					ownerType:         values.ownerType,
					phone:             values.phone,
					rooms:             values.rooms,
					roomType:          values.roomType,
					area:              values.totalArea    != null ? String(values.totalArea)    : undefined,
					livingArea:        values.livingArea   != null ? String(values.livingArea)   : undefined,
					kitchenSpace:      values.kitchenSpace != null ? String(values.kitchenSpace) : undefined,
					floor:             values.floor,
					totalFloors:       values.totalFloors,
					balcony:           values.balcony,
					loggia:            values.loggia,
					wardrobe:          values.wardrobe,
					bathroom:          values.bathroomType,
					bathroomCount:     values.bathroomType === 'SEVERAL' ? values.bathroomCount : undefined,
					renovation:        values.renovation,
					furniture:         values.furniture,
					appliances:        values.appliances,
					internet:          values.internet,
					yearBuilt:         values.yearBuilt,
					buildingType:      values.buildingType,
					concierge:         values.concierge,
					chute:             values.chute,
					gas:               values.gas,
					passengerElevator: elevatorFromEnum(values.passengerElevators),
					freightElevator:   elevatorFromEnum(values.freightElevators),
					parking:           values.parking,
					parkingType:       values.parkingType,
					yard:              values.yard,
					maxTenants:        values.maxTenants,
					childrenAllowed:   values.childrenAllowed,
					petsAllowed:       values.petsAllowed,
					smokingAllowed:    values.smokingAllowed,
					address:           values.address,
					city:              values.city,
					latitude:          values.latitude,
					longitude:         values.longitude,
					description:       values.description?.trim() || undefined,
				},
			}).unwrap()
			resetDraft()
			onOpenChange(false)
		} catch (err) {
			setSubmitError(extractErrorMessage(err) ?? 'Не удалось сохранить изменения')
		}
	}

	/**
	 * `phone`, `ownerType` and `apartmentNumber` are validated but have no
	 * input in this sheet — they are only round-tripped so the update does not
	 * drop them. A stored value that fails validation (a legacy free-form
	 * phone, say) would otherwise make Save do nothing at all: no message, no
	 * field to correct, no way to edit the property ever again. Surface those
	 * errors in the sheet's own error slot instead.
	 */
	function reportUnrenderedErrors(errors: FieldErrors<EditPropertyFormValues>) {
		const messages = (['phone', 'ownerType', 'apartmentNumber'] as const)
			.map((field) => errors[field]?.message)
			.filter((m): m is string => !!m)
		if (messages.length) setSubmitError(messages.map((m) => t(m)).join('; '))
	}

	return (
		<>
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<SheetContent side="right" className="!max-w-xl w-full p-0 flex flex-col">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit, reportUnrenderedErrors)}
						className="flex h-full flex-col"
					>
						<header className="flex items-center border-b border-(--color-border) px-6 py-4">
							<h2 className="text-lg font-[500]">
								{t('properties.editDialog.title', { defaultValue: 'Редактирование объекта' })}
							</h2>
						</header>

						<div className="flex-1 overflow-y-auto px-6 py-5">
							<div className="flex flex-col gap-6">
								<section className="flex flex-col gap-3">
									<FormLabel className="text-base font-[500]">{t('properties.form.propertyType', { defaultValue: 'Тип объекта' })}</FormLabel>
									<FormField
										control={form.control}
										name="type"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<RadioGroup
														value={field.value}
														name={field.name}
														onValueChange={(v) => {
															field.onChange(v)
															// The same list the wizard uses, so neither window can fall behind
															// the other: a stale commercial answer resent on save would
															// override the server's own reset.
															applyFieldResets(form.setValue, TYPE_SPECIFIC_RESETS)
														}}
														className="flex flex-wrap gap-2"
													>
														{CREATE_PROPERTY_TYPES.map((value) => (
															<RadioOption key={value} value={value} id={`edit-type-${value}`} label={t(`properties.types.${value}`)} />
														))}
													</RadioGroup>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</section>

								<Step2Details />

								<section className="flex flex-col gap-3">
									<FormLabel className="text-base font-[500]">Расположение</FormLabel>
									<FormField
										control={form.control}
										name="city"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('properties.form.city')}</FormLabel>
												<FormControl><Input {...field} /></FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="address"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('properties.form.address')}</FormLabel>
												<FormControl>
													<AddressSuggest
														value={field.value}
														onChange={handleAddressTyped}
														onSelect={handleAddressSelect}
														onBlur={field.onBlur}
														name={field.name}
														allowStreetLevel={property.type === 'LAND'}
														placeholder={t('properties.form.addressPlaceholder')}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<Button
										type="button"
										variant="link"
										size="sm"
										className="h-auto self-start p-0 text-(--color-brand)"
										onClick={() => setMapOpen(true)}
									>
										{form.watch('latitude') != null && form.watch('longitude') != null
											? t('properties.form.changeOnMap')
											: t('properties.form.showOnMap')}
									</Button>
									<MapPicker
										open={mapOpen}
										onOpenChange={setMapOpen}
										initialLat={form.watch('latitude')}
										initialLng={form.watch('longitude')}
										allowStreetLevel={property.type === 'LAND'}
										onSelect={(r) => {
											form.setValue('latitude',  r.latitude,  { shouldDirty: true, shouldValidate: true })
											form.setValue('longitude', r.longitude, { shouldDirty: true, shouldValidate: true })
											if (r.city)    form.setValue('city',    r.city,    { shouldDirty: true, shouldValidate: true })
											if (r.address) form.setValue('address', r.address, { shouldDirty: true, shouldValidate: true })
										}}
									/>
								</section>

								<section className="flex flex-col gap-3">
									<FormLabel className="text-base font-[500]">Описание</FormLabel>
									<FormField
										control={form.control}
										name="description"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<Textarea
														{...field}
														value={field.value ?? ''}
														rows={5}
														placeholder="Краткое описание объекта…"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</section>
							</div>
						</div>

						{submitError && (
							<div className="px-6 pb-2">
								<Alert variant="destructive">
									<AlertDescription>{submitError}</AlertDescription>
								</Alert>
							</div>
						)}
						<footer className="flex items-center justify-end gap-2 border-t border-(--color-border) px-6 py-4">
							<Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
								{t('common.cancel')}
							</Button>
							<Button type="submit" disabled={isLoading}>
								{t('common.save')}
							</Button>
						</footer>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
		<ConfirmDialog
			open={confirmOpen}
			onOpenChange={(v) => { if (!v) handleCancelDiscard() }}
			title={t('common.unsavedChanges.title')}
			description={t('common.unsavedChanges.description')}
			confirmLabel={t('common.unsavedChanges.confirm')}
			cancelLabel={t('common.unsavedChanges.cancel')}
			variant="destructive"
			onConfirm={handleConfirmDiscard}
		/>
		</>
	)
}
